const VENUE_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Stable slug overrides for marketing links and venues whose auto-slug would be ambiguous.
 * Keys are venue UUIDs; values are the canonical path segment (e.g. "kasa-cafe").
 */
export const VENUE_SLUG_BY_ID = {
  '660e8400-e29b-41d4-a716-446655440007': 'kasa-cafe',
  '660e8400-e29b-41d4-a716-446655440023': 'kiju-coffee',
  '660e8400-e29b-41d4-a716-446655440002': 'anomalous-space',
};

export const KASA_CAFE_VENUE_ID = '660e8400-e29b-41d4-a716-446655440007';
export { ANOMALOUS_SPACE_VENUE_ID } from './venueContactOverrides';

/** Legacy/auto-generated slugs that should still resolve to a venue UUID. */
const VENUE_SLUG_ALIASES = {
  'anomalous-space-the-home': '660e8400-e29b-41d4-a716-446655440002',
};

const VENUE_ID_BY_SLUG = {
  ...Object.fromEntries(
    Object.entries(VENUE_SLUG_BY_ID).map(([id, slug]) => [slug.toLowerCase(), id])
  ),
  ...VENUE_SLUG_ALIASES,
};

let slugToIdIndex = null;
let slugIndexPromise = null;

export function isVenueUuid(value) {
  return VENUE_UUID_REGEX.test(String(value || '').trim());
}

/** Kasa Café — custom venue-details UX (no search bar / back link; “Continue to next step” CTA). */
export function isKasaCafeVenue(venueOrPathParam) {
  if (!venueOrPathParam) return false;

  if (typeof venueOrPathParam === 'object') {
    const id = String(venueOrPathParam.id ?? venueOrPathParam.venue_id ?? '').trim();
    if (id === KASA_CAFE_VENUE_ID) return true;
    return getVenueSlug(venueOrPathParam) === 'kasa-cafe';
  }

  const param = String(venueOrPathParam).trim();
  if (param === KASA_CAFE_VENUE_ID) return true;
  if (param.toLowerCase() === 'kasa-cafe') return true;
  return resolveVenueIdFromSlugSync(param) === KASA_CAFE_VENUE_ID;
}

export function slugifyVenueName(name) {
  return (
    String(name || '')
      .toLowerCase()
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 80) || 'venue'
  );
}

export function getVenueSlug(venue) {
  if (!venue) return '';

  const id = String(venue.id ?? venue.venue_id ?? '').trim();
  if (id && VENUE_SLUG_BY_ID[id]) {
    return VENUE_SLUG_BY_ID[id];
  }

  const fromApi = venue.slug ?? venue.url_slug;
  if (fromApi) {
    return String(fromApi).toLowerCase().trim();
  }

  return slugifyVenueName(venue.name ?? venue.venue_name ?? '');
}

function getVenueSlugVariants(venue) {
  const slugs = new Set();
  const canonical = getVenueSlug(venue);
  if (canonical) slugs.add(canonical.toLowerCase());
  const fromName = slugifyVenueName(venue.name ?? venue.venue_name ?? '');
  if (fromName) slugs.add(fromName.toLowerCase());
  return slugs;
}

export function resolveVenueIdFromSlugSync(pathParam) {
  const param = String(pathParam || '').trim();
  if (!param) return null;
  if (isVenueUuid(param)) return param;
  return VENUE_ID_BY_SLUG[param.toLowerCase()] ?? null;
}

async function buildSlugToIdIndex(venueApiService) {
  const venues = await venueApiService.getAllVenues();
  const index = { ...VENUE_ID_BY_SLUG };

  for (const venue of venues) {
    const id = String(venue.id ?? venue.venue_id ?? '').trim();
    if (!id) continue;

    for (const slug of getVenueSlugVariants(venue)) {
      if (!index[slug]) {
        index[slug] = id;
      }
    }
  }

  return index;
}

export async function resolveVenueIdFromPathParam(pathParam, venueApiService) {
  const syncId = resolveVenueIdFromSlugSync(pathParam);
  if (syncId) return syncId;

  const param = String(pathParam || '').trim().toLowerCase();
  if (!param) return null;

  if (!slugToIdIndex) {
    if (!slugIndexPromise) {
      slugIndexPromise = buildSlugToIdIndex(venueApiService)
        .then((index) => {
          slugToIdIndex = index;
          return index;
        })
        .finally(() => {
          slugIndexPromise = null;
        });
    }
    await slugIndexPromise;
  }

  return slugToIdIndex?.[param] ?? null;
}

export function buildVenuePath(venueOrId, options = {}) {
  const { searchParams, venueName } = options;

  let slug = '';
  if (typeof venueOrId === 'string') {
    const id = venueOrId.trim();
    slug =
      VENUE_SLUG_BY_ID[id] ||
      slugifyVenueName(venueName || '');
  } else if (venueOrId) {
    slug = getVenueSlug(venueOrId);
  }

  let path = `/venue/${slug || 'venue'}`;

  if (searchParams) {
    const queryString =
      searchParams instanceof URLSearchParams
        ? searchParams.toString()
        : typeof searchParams === 'string'
          ? searchParams.replace(/^\?/, '')
          : new URLSearchParams(searchParams).toString();

    if (queryString) {
      path += `?${queryString}`;
    }
  }

  return path;
}
